import { Component, Vue } from "vue-property-decorator";
import {
  GLOSSARY_CATEGORIES_QUERY,
  GLOSSARY_ABBREVIATIONS_QUERY,
  GLOSSARY_TERMS_QUERY,
} from "@/graphql/glossary-query";
import { gql } from "graphql-tag";
import AppLoaderComponent from "../app-loader/app-loader.component.vue";

// The "ALL" tab is a catch-all: it shows acronyms and terms across every
// program instead of filtering by a single one.
const ALL_TAB = "ALL";

@Component({
  name: "GlossaryComponent",
  components: {
    AppLoaderComponent,
  },
})
export default class GlossaryComponent extends Vue {
  // Tabs + per-program metadata are driven by the Glossary Category collection
  // (Strapi), not hardcoded — so categories can be managed from the admin.
  public headerData: any[] = [];
  public programConfig: any = {};
  // program name -> category seq_id, used to order the ALL tab by category.
  public categoryOrder: any = {};

  public currentTab = "";
  public allAbbreviations: any[] = [];
  public allTerms: any[] = [];
  public abbreviations: any[] = [];
  public terms: any[] = [];
  public isLoading = false;

  public get currentProgram() {
    return (
      this.programConfig[this.currentTab] || {
        fullName: "",
        subtitle: "",
        color: "",
      }
    );
  }

  public async created() {
    this.isLoading = true;
    try {
      const [categoriesResult, abbrevResult, termsResult] = await Promise.all([
        this.$apollo.query({
          query: gql`
            query ${GLOSSARY_CATEGORIES_QUERY}
          `,
        }),
        this.$apollo.query({
          query: gql`
            query ${GLOSSARY_ABBREVIATIONS_QUERY}
          `,
        }),
        this.$apollo.query({
          query: gql`
            query ${GLOSSARY_TERMS_QUERY}
          `,
        }),
      ]);

      const categories = categoriesResult.data.glossaryCategories.data;
      this.headerData = categories.map((c: any) => ({
        label: c.attributes.name,
        color: c.attributes.color,
      }));
      this.programConfig = categories.reduce((acc: any, c: any) => {
        acc[c.attributes.name] = {
          fullName: c.attributes.full_name,
          subtitle: c.attributes.subtitle,
          color: c.attributes.color,
        };
        return acc;
      }, {});
      this.categoryOrder = categories.reduce((acc: any, c: any) => {
        acc[c.attributes.name] = c.attributes.seq_id || 0;
        return acc;
      }, {});
      this.currentTab = categories.length ? categories[0].attributes.name : "";

      this.allAbbreviations = abbrevResult.data.glossaryAbbreviations.data;
      this.allTerms = termsResult.data.glossaryTerms.data;
      this.filterData();
    } finally {
      this.isLoading = false;
    }
  }

  public onNavigation(tab: string) {
    this.currentTab = tab;
    this.filterData();
  }

  public filterData() {
    // The ALL tab shows everything; other tabs filter by their program.
    const showAll = this.currentTab === ALL_TAB;

    let abbreviations = this.allAbbreviations.filter(
      (item: any) => showAll || item.attributes.program === this.currentTab
    );
    const terms = this.allTerms.filter(
      (item: any) => showAll || item.attributes.program === this.currentTab
    );

    // Abbreviations: the same acronym can appear under several programs, so on
    // the ALL tab collapse repeats to show each once.
    if (showAll) {
      abbreviations = this.dedupeBy(
        abbreviations,
        (item: any) => item.attributes.short_form
      );
    }

    // Abbreviations are always alphabetical by short_form.
    this.abbreviations = abbreviations.sort((a: any, b: any) =>
      (a.attributes.short_form || "").localeCompare(
        b.attributes.short_form || "",
        undefined,
        { sensitivity: "base" }
      )
    );

    // Terms: per-program tabs use the curated seq_id order. The ALL tab keeps
    // every program's full list (no de-dup), grouped by the category order
    // (CPRS, CFSS, CYAPSS via category seq_id), each program in its seq_id order.
    this.terms = terms.sort((a: any, b: any) => {
      if (showAll) {
        const orderA = this.categoryOrder[a.attributes.program] || 0;
        const orderB = this.categoryOrder[b.attributes.program] || 0;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
      }
      return (a.attributes.seq_id || 0) - (b.attributes.seq_id || 0);
    });
  }

  // Keep the first occurrence of each key (case-insensitive), drop later ones.
  private dedupeBy(items: any[], keyFn: (item: any) => string): any[] {
    const seen = new Set<string>();
    return items.filter((item: any) => {
      const key = (keyFn(item) || "").trim().toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }
}
